import React from "react";
import { AbsoluteFill, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { Bg, DISPLAY, INK, LIME, PINK, popIn, PURPLE, UI, WHITE, YELLOW } from "./theme";

/* ════════════════════════════════════════════════════════════════════════
   SCENE 3b — FEATURE 2 (frame 375-480 / 12.5-16s, 105 frame)
   "Hitung Komisi Kapster Otomatis" — koin terbelah + bar pembagian.
   ════════════════════════════════════════════════════════════════════════ */

const COIN = 420; // diameter koin

export const Scene3bKomisi: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const head = popIn(frame, fps, 0, { damping: 10, stiffness: 300, mass: 0.9 });
  const coinIn = spring({ frame, fps, config: { damping: 11, stiffness: 220, mass: 1 } });
  const coinScale = interpolate(coinIn, [0, 1], [0, 1]);
  // Koin terbelah pada frame 34.
  const split = spring({ frame: frame - 34, fps, config: { damping: 13, stiffness: 200, mass: 1 } });
  const gap = interpolate(split, [0, 1], [0, 100]);
  const spin = interpolate(frame, [0, 105], [0, 8]);

  const bar1 = spring({ frame: frame - 58, fps, config: { damping: 14, stiffness: 180, mass: 0.9 } });
  const bar2 = spring({ frame: frame - 66, fps, config: { damping: 14, stiffness: 180, mass: 0.9 } });
  const total = popIn(frame, fps, 82, { damping: 10, stiffness: 340, mass: 0.85 });

  const half = (side: "left" | "right") => (
    <div
      className="absolute rounded-full border-[6px] border-black"
      style={{
        background: YELLOW,
        width: COIN,
        height: COIN,
        left: (980 - COIN) / 2,
        top: 10,
        clipPath: side === "left" ? "inset(0 50% 0 0)" : "inset(0 0 0 50%)",
        transform: `translateX(${side === "left" ? -gap : gap}px) rotate(${spin}deg)`,
        boxShadow: "8px 8px 0px 0px rgba(0,0,0,1)",
      }}
    />
  );

  return (
    <AbsoluteFill style={{ fontFamily: UI, color: INK }}>
      <Bg color={PINK} pattern="grid" patternColor={WHITE} patternOpacity={0.2}>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 px-[40px]">
          {/* ── Headline ── */}
          <div
            className="flex flex-col items-center gap-3"
            style={{ transform: `scale(${head.scale})`, opacity: head.opacity }}
          >
            <div
              className="border-4 border-black bg-black px-7 py-1 text-[70px] uppercase leading-none text-white shadow-[8px_8px_0px_0px_rgba(255,255,255,1)]"
              style={{ fontFamily: DISPLAY }}
            >
              HITUNG KOMISI
            </div>
            <div
              className="border-4 border-black bg-[#B8FF2F] px-7 py-1 text-[70px] uppercase leading-none text-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
              style={{ fontFamily: DISPLAY }}
            >
              KAPSTER OTOMATIS
            </div>
          </div>

          {/* ── Koin terbelah ── */}
          <div className="relative w-[980px]" style={{ height: 560, transform: `scale(${coinScale})` }}>
            {half("left")}
            {half("right")}
            {/* label "Rp" tengah — hilang setelah koin terbelah */}
            <div
              className="absolute text-center"
              style={{
                left: 0,
                top: 110,
                width: 980,
                opacity: interpolate(split, [0, 0.3], [1, 0], { extrapolateRight: "clamp" }),
                fontFamily: DISPLAY,
                fontSize: 160,
                lineHeight: 1,
              }}
            >
              Rp
            </div>
            {/* label hasil belah */}
            {[
              { side: "left" as const, text: "60% KAPSTER", bg: LIME, tx: -gap },
              { side: "right" as const, text: "40% OWNER", bg: PURPLE, tx: gap },
            ].map((l) => (
              <div
                key={l.text}
                className="absolute border-4 border-black px-5 py-2 text-[34px]"
                style={{
                  top: 452,
                  left: l.side === "left" ? 180 : 520,
                  background: l.bg,
                  color: l.side === "left" ? INK : WHITE,
                  transform: `translateX(${l.tx}px)`,
                  opacity: interpolate(split, [0.35, 0.85], [0, 1], { extrapolateLeft: "clamp" }),
                  fontFamily: DISPLAY,
                  whiteSpace: "nowrap",
                  boxShadow: "6px 6px 0px 0px rgba(0,0,0,1)",
                }}
              >
                {l.text}
              </div>
            ))}
          </div>

          {/* ── Bar pembagian ── */}
          <div className="w-[940px]">
            <div className="flex h-[130px] w-full border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
              <div
                className="flex items-center justify-center overflow-hidden border-r-4 border-black bg-[#B8FF2F]"
                style={{ width: `${60 * bar1}%` }}
              >
                <div className="whitespace-nowrap px-2 text-[36px]" style={{ fontFamily: DISPLAY }}>
                  KAPSTER 60%
                </div>
              </div>
              <div
                className="flex items-center justify-center overflow-hidden bg-[#B026FF]"
                style={{ width: `${40 * bar2}%` }}
              >
                <div className="whitespace-nowrap px-2 text-[36px] text-white" style={{ fontFamily: DISPLAY }}>
                  OWNER 40%
                </div>
              </div>
            </div>

            {/* ── Kartu total komisi ── */}
            <div
              className="mt-7 flex items-center justify-between border-4 border-black bg-white px-6 py-4 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]"
              style={{ transform: `scale(${total.scale})`, opacity: total.opacity }}
            >
              <div>
                <div className="text-[26px] uppercase tracking-wider text-black/60" style={{ fontFamily: DISPLAY }}>
                  KOMISI HARI INI
                </div>
                <div className="text-[26px] text-black">Dibagi otomatis, tanpa hitung manual</div>
              </div>
              <div
                className="border-4 border-black bg-[#FFE14D] px-5 py-2 text-[58px]"
                style={{ fontFamily: DISPLAY }}
              >
                Rp 420.000
              </div>
            </div>
          </div>
        </div>
      </Bg>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
